import React from "react";
import { useI18n } from "../i18n";

const en = {
  privacy: {
    title: "Privacy Policy",
    sections: [
      ["What we collect", "We collect basic account info (name, email), order data, addresses, and analytics events. We never sell your personal data."],
      ["Why we collect it", "To process orders, ship parcels via PostNL/DHL, prevent fraud, and improve our service."],
      ["Your GDPR rights", "You can request access, correction, deletion, portability, and object to processing. Contact privacy@snapshop.it.com."],
      ["Cookies", "Essential cookies make the site work. Analytics cookies are optional via our banner."],
    ],
  },
  terms: {
    title: "Terms & Conditions",
    sections: [
      ["The agreement", "By using SnapShop, you accept these terms. The agreement is governed by Dutch law."],
      ["Prices & VAT", "All prices include 21% Dutch VAT unless stated otherwise. Shipping is shown at checkout."],
      ["Right of withdrawal", "Consumers in the EU have a 14-day right of withdrawal, in line with Article 6:230o BW."],
      ["Liability", "We do not exclude liability for gross negligence or willful misconduct."],
    ],
  },
  returns: {
    title: "Returns & Refunds",
    sections: [
      ["30-day returns", "We accept returns within 30 days from delivery. Items must be in original condition."],
      ["How to start", "Email returns@snapshop.it.com with your order ID. We send a prepaid PostNL label for orders above €50."],
      ["Refund time", "Refunds are issued via the original payment method within 14 days of receipt."],
    ],
  },
  shipping: {
    title: "Shipping Policy",
    sections: [
      ["Where we ship", "We currently ship within the Netherlands. Belgium and Germany are coming soon."],
      ["Carriers", "We use PostNL (default) and DHL Netherlands. You can choose at checkout."],
      ["Delivery times", "PostNL Standard 2-3 business days. PostNL Express next day. DHL 2-4 business days."],
      ["Free shipping", "Free PostNL Standard from €50. Free DHL Standard from €75."],
      ["Tracking", "You receive a tracking code by email when your order ships. Track it on our Track Order page."],
    ],
  },
  faq: {
    title: "FAQ",
    sections: [
      ["Shipping in NL", "PostNL standard 2-3 working days. Free above €50. Express next day."],
      ["Payment options", "iDEAL (primary), credit card, PayPal, Apple Pay, Google Pay, Klarna Pay Later."],
      ["International", "We currently ship within Netherlands. Belgium & Germany are coming soon."],
      ["Track your order", "Use the Track Order page with your tracking code."],
    ],
  },
};
const nl = {
  privacy: {
    title: "Privacybeleid",
    sections: [
      ["Wat we verzamelen", "Basis accountgegevens (naam, e-mail), bestelgegevens, adressen en analytics. We verkopen je gegevens nooit."],
      ["Waarom", "Om bestellingen te verwerken, pakketten via PostNL/DHL te versturen, fraude te voorkomen en onze service te verbeteren."],
      ["Jouw AVG-rechten", "Je hebt recht op inzage, correctie, verwijdering, portabiliteit en bezwaar. Mail naar privacy@snapshop.it.com."],
      ["Cookies", "Essentiële cookies zijn noodzakelijk. Analytics is optioneel via de banner."],
    ],
  },
  terms: {
    title: "Algemene voorwaarden",
    sections: [
      ["De overeenkomst", "Door gebruik van SnapShop accepteer je deze voorwaarden. Nederlands recht is van toepassing."],
      ["Prijzen & BTW", "Alle prijzen zijn inclusief 21% Nederlandse BTW, tenzij anders vermeld."],
      ["Herroepingsrecht", "Consumenten hebben recht op 14 dagen herroeping (art. 6:230o BW)."],
      ["Aansprakelijkheid", "Wij sluiten geen aansprakelijkheid uit voor grove nalatigheid of opzet."],
    ],
  },
  returns: {
    title: "Retouren & restituties",
    sections: [
      ["30 dagen retour", "Retourneren binnen 30 dagen na levering. Artikelen moeten in originele staat zijn."],
      ["Hoe te starten", "Mail naar returns@snapshop.it.com met je bestelnummer. Voor orders boven €50 sturen we een gratis PostNL-label."],
      ["Restitutie", "Restitutie via originele betaalmethode binnen 14 dagen na ontvangst."],
    ],
  },
  shipping: {
    title: "Verzendbeleid",
    sections: [
      ["Waar we verzenden", "Op dit moment alleen Nederland. België en Duitsland volgen binnenkort."],
      ["Vervoerders", "Wij verzenden met PostNL (standaard) en DHL Nederland. Je kiest bij afrekenen."],
      ["Levertijden", "PostNL Standaard 2-3 werkdagen. PostNL Express de volgende werkdag. DHL 2-4 werkdagen."],
      ["Gratis verzending", "Gratis PostNL Standaard vanaf €50. Gratis DHL Standaard vanaf €75."],
      ["Tracking", "Je ontvangt per e-mail een trackingcode zodra je pakket onderweg is. Volg op onze 'Volg bestelling'-pagina."],
    ],
  },
  faq: {
    title: "Veelgestelde vragen",
    sections: [
      ["Verzending in NL", "PostNL standaard 2-3 werkdagen. Gratis vanaf €50. Express de volgende dag."],
      ["Betaalmethoden", "iDEAL (primair), creditcard, PayPal, Apple Pay, Google Pay, Klarna achteraf betalen."],
      ["Internationaal", "Op dit moment alleen Nederland. België en Duitsland volgen binnenkort."],
      ["Volg je bestelling", "Gebruik de pagina 'Volg bestelling' met je trackingcode."],
    ],
  },
};

const StaticPage = ({ kind }) => {
  const { lang } = useI18n();
  const block = (lang === "nl" ? nl : en)[kind];
  return (
    <div className="max-w-[900px] mx-auto px-6 py-16" data-testid={`static-${kind}`}>
      <h1 className="font-display text-5xl md:text-6xl tracking-tighter font-light mb-10">{block.title}</h1>
      <div className="space-y-8">
        {block.sections.map(([title, body], i) => (
          <div key={i}>
            <h2 className="font-display text-2xl">{title}</h2>
            <p className="mt-2 text-ink-muted leading-relaxed">{body}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export const PrivacyPage = () => <StaticPage kind="privacy" />;
export const TermsPage = () => <StaticPage kind="terms" />;
export const ReturnsPage = () => <StaticPage kind="returns" />;
export const ShippingPage = () => <StaticPage kind="shipping" />;
export const FaqPage = () => <StaticPage kind="faq" />;
